'use client';

import React, { useMemo } from 'react';
import { AlertTriangle, CheckCircle2, X } from 'lucide-react';
import type { BuilderPage } from '@/lib/storefront/section-library';
import { buildSeoChecklist } from '@/lib/storefront/seo-checklist';
import type { SitePageLink } from '@/lib/site/site-links';

interface SeoChecklistPanelProps {
  open: boolean;
  page: BuilderPage;
  pageSlug: string;
  sitePages: SitePageLink[];
  onClose: () => void;
}

export function SeoChecklistPanel({ open, page, pageSlug, sitePages, onClose }: SeoChecklistPanelProps) {
  const issues = useMemo(() => buildSeoChecklist(page, pageSlug, sitePages), [page, pageSlug, sitePages]);
  if (!open) return null;

  const errors = issues.filter((issue) => issue.severity === 'error');
  const warnings = issues.filter((issue) => issue.severity === 'warning');

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-labelledby="seo-checklist-title">
      <div className="w-full max-w-md overflow-hidden rounded-2xl border border-line bg-card shadow-xl">
        <div className="flex items-center justify-between border-b border-line px-4 py-3">
          <div>
            <h2 id="seo-checklist-title" className="text-sm font-semibold text-ink">SEO checklist</h2>
            <p className="text-[11px] text-muted">Errors block confidence; warnings are recommendations.</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-muted hover:bg-shell" aria-label="Close SEO checklist">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="max-h-[60vh] space-y-3 overflow-y-auto p-4">
          {issues.length === 0 ? (
            <div className="flex items-start gap-2 rounded-xl border border-success/30 bg-success/10 px-3 py-3 text-xs text-ink">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" />
              <p>No checklist issues on this page. This is not a ranking guarantee.</p>
            </div>
          ) : (
            <>
              {errors.map((issue) => (
                <div key={issue.id} className="flex gap-2 rounded-xl border border-danger/30 bg-danger/10 px-3 py-2.5 text-xs text-ink">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-danger" />
                  <div>
                    <p className="font-semibold text-danger">Error</p>
                    <p className="mt-0.5 text-muted">{issue.message}</p>
                  </div>
                </div>
              ))}
              {warnings.map((issue) => (
                <div key={issue.id} className="flex gap-2 rounded-xl border border-warning/30 bg-warning/10 px-3 py-2.5 text-xs text-ink">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning" />
                  <div>
                    <p className="font-semibold text-warning">Recommendation</p>
                    <p className="mt-0.5 text-muted">{issue.message}</p>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
